<template>
  <div data-nav="doc-list-nav">
    <div data-header="docs-list" class="list-actions">
      <UserMenu />
      <IconButton
        v-if="connected"
        icon-name="add"
        text="New Document"
        @click="addDocument"
      />
      <p v-else data-status="warning">
        Cannot add or open documents while offline.
      </p>
    </div>

    <nav class="list-body documents-list">
      <ul data-list="plain documents">
        <DocumentListItem
          v-for="document in orderedDocuments"
          :key="document.id"
          :document="document"
          :is-open="documentIsOpen(document.id)"
          :is-editing="editing === document.id"
          :connected="connected"
          @sharing="(docId) => (sharingDocumentId = docId)"
          @archiving="(docId) => (archivingDocumentId = docId)"
        />
      </ul>
      <span v-if="!orderedDocuments.length" data-message="empty">
        You have no documents yet. Add a new document to get started!
      </span>
    </nav>
  </div>

  <DocumentArchiveModal
    v-if="archivingDocument"
    :document-id="archivingDocument.id"
    :document-title="archivingDocument.title"
    :document-is-shared="!!archivingDocument.public_id"
    :connected="connected"
    @close="archivingDocumentId = null"
  />

  <DocumentShareModal
    v-if="sharingDocument"
    :version-id="sharingDocument.id"
    is-current
    :public-id="sharingDocument.public_id"
    :connected="connected"
    @close="sharingDocumentId = null"
  />
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

import DocumentArchiveModal from '@/js/components/document/archive-modal.vue';
import DocumentListItem from '@/js/components/document/list-item.vue';
import DocumentShareModal from '@/js/components/document/share-modal.vue';
import UserMenu from '@/js/components/navigation/user-menu.vue';
import { useDocumentsStore } from '@/js/stores/documents';
import { useSocketStore } from '@/js/stores/socket';

export default defineComponent({
  name: 'DocumentList',
  components: {
    DocumentArchiveModal,
    DocumentListItem,
    DocumentShareModal,
    UserMenu,
  },
  setup() {
    const archivingDocumentId = ref<number | null>(null);
    const sharingDocumentId = ref<number | null>(null);
    const socketStore = useSocketStore();
    const documentStore = useDocumentsStore();
    const connected = computed(() => socketStore.connected);
    const editing = computed(() => documentStore.editing);
    const documentIsOpen = computed(() => documentStore.documentIsOpen);
    const orderedDocuments = computed(() => documentStore.orderedDocuments);
    const addDocument = computed(() => documentStore.addDocument);
    const archivingDocument = computed(() =>
      archivingDocumentId.value === null
        ? null
        : documentStore.documentById(archivingDocumentId.value),
    );
    const sharingDocument = computed(() =>
      sharingDocumentId.value === null
        ? null
        : documentStore.documentById(sharingDocumentId.value),
    );

    return {
      addDocument,
      archivingDocument,
      archivingDocumentId,
      connected,
      documentIsOpen,
      editing,
      orderedDocuments,
      sharingDocument,
      sharingDocumentId,
    };
  },
  watch: {
    archivingDocument(doc) {
      // Modal is removed before close event can be called, so clean up and
      // remove archivingDocumentId when the document is archived and removed
      // from the store. Otherwise, the archive modal is re-opened when the
      // document is unarchived and returned to the store.
      if (doc === null) {
        this.archivingDocumentId = null;
      }
    },
  },
});
</script>
